import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Spin, message } from "antd";
import { EyeInvisibleOutlined, EyeOutlined } from "@ant-design/icons";
import {
  useLoginMutation,
  useLoginWithOtpMutation,
} from "../../../store/service/authService";
import { APP_BRAND } from "../../../utils/brand";
import PreLoginLobby from "../preLoginLobby/PreLoginLobby";

const Signin = () => {
  const [trigger] = useLoginMutation();
  const [triggerWotp, { isLoading }] = useLoginWithOtpMutation();
  const nav = useNavigate();

  const [showLogin, setShowLogin] = useState(false);
  const [showOtp, setShowOtp] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [authTip, setAuthTip] = useState("");
  const [formData, setFormData] = useState({
    username: "",
    password: "",
    OTP: "",
  });

  const hostname = window.location.hostname;
  const loginDomainOverride = import.meta.env.VITE_APP_DOMAIN || "admin.antpro.co";
  const resolvedHost =
    hostname === "localhost" || hostname === "127.0.0.1" ? loginDomainOverride : hostname;
  const url = resolvedHost.includes("madmin")
    ? `sub.${resolvedHost.split(".")[1]}.${resolvedHost.split(".")[2]}`
    : resolvedHost;

  const persistAdminSession = (res) => {
    const ut = String(res?.userTypeInfo) === "99" ? "7" : res?.userTypeInfo;
    localStorage.setItem("token", res?.token);
    localStorage.setItem("rulesStatus", true);
    localStorage.setItem("userId", res?.userId);
    localStorage.setItem("userType", ut);
    localStorage.setItem("username", res?.username || "Super Admin");
    localStorage.setItem("ps", res?.ps);
    if (Number(res?.maint) === 1) {
      localStorage.setItem("_m", "1");
    } else {
      localStorage.removeItem("_m");
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setAuthTip("");

    const authPayload = {
      userId: String(formData.username || "").trim(),
      password: String(formData.password || "").trim(),
      url,
    };

    if (!authPayload.userId) {
      setAuthTip("Enter your User ID");
      return;
    }
    if (!authPayload.password) {
      setAuthTip("Enter your password");
      return;
    }
    if (showOtp && !String(formData.OTP || "").trim()) {
      setAuthTip("Enter OTP");
      return;
    }

    try {
      if (showOtp) {
        const res = await triggerWotp({ ...authPayload, otp: formData.OTP });
        if (res?.data?.token) {
          persistAdminSession(res.data);
          nav("/dashboard");
          return;
        }
        if (res?.error) {
          setAuthTip(res?.error?.data?.message || "Login failed");
        }
        return;
      }

      const res = await trigger(authPayload).unwrap();
      if (res?.token) {
        if (String(res?.userTypeInfo) === "2") {
          localStorage.setItem("client-token", res?.token);
          localStorage.setItem("client-userId", res?.userId);
          localStorage.setItem("client-username", res?.username);
          localStorage.removeItem("_m");
          nav("/play");
          return;
        }
        persistAdminSession(res);
        nav("/dashboard");
        return;
      }

      if (res?.status) {
        setShowOtp(true);
        message.info("Enter OTP to continue");
      } else {
        setAuthTip(res?.message || "Login failed");
      }
    } catch (err) {
      setAuthTip(err?.data?.message || "Invalid User ID or password");
    }
  };

  useEffect(() => {
    if (localStorage.getItem("token") !== null) {
      nav("/dashboard");
    }
  }, [nav]);

  const updateField = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (authTip) setAuthTip("");
  };

  return (
    <PreLoginLobby
      loginOpen={showLogin}
      onOpenLogin={() => setShowLogin(true)}
      onCloseLogin={() => setShowLogin(false)}
      headerLogin={
        <form className="w777-header-login-form" onSubmit={onSubmit} noValidate>
          <input
            className="w777-h-input"
            type="text"
            placeholder="Username*"
            autoComplete="username"
            value={formData.username}
            onChange={(ev) => updateField("username", ev.target.value)}
          />
          <div className="w777-h-pass-wrap">
            <input
              className="w777-h-input"
              type={showPassword ? "text" : "password"}
              placeholder="Password*"
              autoComplete="current-password"
              value={formData.password}
              onChange={(ev) => updateField("password", ev.target.value)}
            />
            <button
              type="button"
              className="w777-eye-btn"
              onClick={() => setShowPassword((v) => !v)}
              aria-label={showPassword ? "Hide password" : "Show password"}
              tabIndex={-1}>
              {showPassword ? <EyeOutlined /> : <EyeInvisibleOutlined />}
            </button>
          </div>
          {showOtp ? (
            <input
              className="w777-h-input"
              type="text"
              inputMode="numeric"
              placeholder="OTP*"
              value={formData.OTP}
              onChange={(ev) => updateField("OTP", ev.target.value)}
            />
          ) : null}
          <button type="submit" className="w777-h-submit" disabled={isLoading}>
            {isLoading ? <Spin size="small" /> : "LOGIN"}
          </button>
        </form>
      }
      footer={
        <>
          <p className="w777-lobby-footer-link">
            <Link to="/play/login">Player Login — Match par bet lagayein →</Link>
          </p>
          <p className="w777-lobby-footer-note">Note — This Website Is Not For Indian Territory</p>
          <p className="w777-lobby-footer-age">18+ Only</p>
        </>
      }
      loginModal={
        <form className="w777-login-panel" onSubmit={onSubmit} noValidate>
          <button type="button" className="w777-close-login" onClick={() => setShowLogin(false)}>
            ×
          </button>
          <h2 className="w777-login-title">Admin Login</h2>

          {authTip ? (
            <div className="w777-error-tip" role="alert">
              {authTip}
            </div>
          ) : null}

          <div className="w777-field">
            <label className="w777-label" htmlFor="admin-user-id">
              <span className="w777-req">*</span> User ID
            </label>
            <input
              id="admin-user-id"
              className={`w777-input ${authTip ? "is-error" : ""}`}
              type="text"
              placeholder="Admin User ID"
              autoComplete="username"
              value={formData.username}
              onChange={(ev) => updateField("username", ev.target.value)}
            />
          </div>

          <div className="w777-field">
            <label className="w777-label" htmlFor="admin-password">
              <span className="w777-req">*</span> Password
            </label>
            <div className="w777-input-wrap">
              <input
                id="admin-password"
                className={`w777-input ${authTip ? "is-error" : ""}`}
                type={showPassword ? "text" : "password"}
                placeholder="Password"
                autoComplete="current-password"
                value={formData.password}
                onChange={(ev) => updateField("password", ev.target.value)}
              />
              <button
                type="button"
                className="w777-eye-btn"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? "Hide password" : "Show password"}
                tabIndex={-1}>
                {showPassword ? <EyeOutlined /> : <EyeInvisibleOutlined />}
              </button>
            </div>
          </div>

          {showOtp ? (
            <div className="w777-field">
              <label className="w777-label" htmlFor="admin-otp">
                <span className="w777-req">*</span> OTP
              </label>
              <input
                id="admin-otp"
                className="w777-input"
                type="text"
                inputMode="numeric"
                placeholder="Enter OTP"
                value={formData.OTP}
                onChange={(ev) => updateField("OTP", ev.target.value)}
              />
            </div>
          ) : null}

          <button type="submit" className="w777-submit" disabled={isLoading}>
            {isLoading ? <Spin size="small" /> : showOtp ? "Verify OTP" : "Login"}
          </button>

          <p className="w777-login-hint">Sign in with your {APP_BRAND} admin credentials.</p>
        </form>
      }
    />
  );
};

export default Signin;
